import { ApiError, type ApiClient } from '../api/client.js';
import type { DisplayPreferences, SyncChange } from '../api/types.js';

/**
 * Keeping this tab in step with the account's other devices (M4.3.3).
 *
 * Periodically asks `GET /sync` for what changed since its last position and
 * hands each change to the part of the app that shows it. Every change carries
 * the entity's current state, so applying one twice — including this tab's
 * own changes coming back — is harmless.
 *
 * ## The position is per tab, and survives a reload
 *
 * Kept in `sessionStorage`: one per tab, so two tabs each read everything
 * rather than one advancing past changes the other never applied, and kept
 * across a reload of that tab so an interrupted sync resumes rather than
 * starting again (M4.3.3's third criterion).
 *
 * Full offline storage is out of scope at alpha; this is the protocol and the
 * convergence, not a fourteen-day offline reader.
 */

export interface SyncHandlers {
  /** Read, star or tag state of one item, if it is on screen. */
  readonly onItemState: (itemId: string, data: Record<string, unknown>) => void;
  /** A subscription's baseline, so a mark-all-read elsewhere applies here. */
  readonly onSubscription: (feedId: string, data: Record<string, unknown> | null) => void;
  /** Subscriptions or folders changed: the sidebar needs a fresh read. */
  readonly onStructure: () => void;
  readonly onPreferences: (preferences: Partial<DisplayPreferences>) => void;
  /** The position is past the horizon: everything must be loaded again. */
  readonly onFullResync: () => void;
}

export interface SyncStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** How many batches one pass will read before yielding. */
const MAX_BATCHES = 10;

export class SyncEngine {
  readonly #api: ApiClient;
  readonly #storage: SyncStorage;
  readonly #key: string;
  readonly #handlers: SyncHandlers;
  #running = false;

  constructor(
    api: ApiClient,
    storage: SyncStorage,
    userKey: string,
    handlers: SyncHandlers,
  ) {
    this.#api = api;
    this.#storage = storage;
    this.#key = `feedreader.sync.${userKey}`;
    this.#handlers = handlers;
  }

  /** The stored position, or a fresh one from the server. */
  async start(): Promise<void> {
    if (this.#read() === undefined) {
      const page = await this.#api.sync();
      this.#write(page.nextToken);
    }
  }

  /** Reads and applies everything new. Overlapping calls collapse into one. */
  async tick(): Promise<void> {
    if (this.#running) return;
    this.#running = true;
    try {
      let token = this.#read();
      if (token === undefined) {
        await this.start();
        return;
      }

      for (let batch = 0; batch < MAX_BATCHES; batch += 1) {
        let page;
        try {
          page = await this.#api.sync(token);
        } catch (error) {
          if (error instanceof ApiError && error.status === 410) {
            this.#forget();
            this.#handlers.onFullResync();
            await this.start();
            return;
          }
          throw error;
        }

        this.#apply(page.changes);
        // Per batch, so an interruption resumes from here (M4.3.1).
        token = page.nextToken;
        this.#write(token);
        if (!page.hasMore) break;
      }
    } finally {
      this.#running = false;
    }
  }

  #apply(changes: readonly SyncChange[]): void {
    let structure = false;
    for (const change of changes) {
      switch (change.entity) {
        case 'item_state':
          if (change.data !== null) this.#handlers.onItemState(change.id, change.data);
          break;
        case 'subscription':
          this.#handlers.onSubscription(change.id, change.data);
          structure = true;
          break;
        case 'folder':
          structure = true;
          break;
        case 'profile': {
          const preferences = change.data?.['preferences'];
          if (preferences !== null && typeof preferences === 'object') {
            this.#handlers.onPreferences(preferences as Partial<DisplayPreferences>);
          }
          break;
        }
        case 'archive':
          break;
      }
    }
    if (structure) this.#handlers.onStructure();
  }

  #read(): string | undefined {
    try {
      return this.#storage.getItem(this.#key) ?? undefined;
    } catch {
      return undefined;
    }
  }

  #write(token: string): void {
    try {
      this.#storage.setItem(this.#key, token);
    } catch {
      // A tab that cannot persist its position still syncs; it restarts on reload.
    }
  }

  #forget(): void {
    try {
      this.#storage.removeItem(this.#key);
    } catch {
      // As above.
    }
  }
}
